/* eslint-disable brace-style,no-param-reassign */
/* eslint-disable function-paren-newline */
/* eslint-disable no-mixed-operators */
import { COORDINATE, SNAP_DISTANCE } from '../../../../js/constants';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';

const isVerticalResize = mode => (
    mode !== MODES.LEFT
    && mode !== MODES.RIGHT
    && mode !== MODES.MOVE
);
const isLeftSideResize = mode => (
    mode === MODES.LEFT
    || mode === MODES.TOP_LEFT
    || mode === MODES.BOTTOM_LEFT
);
const isRightSideResize = mode => (
    mode === MODES.RIGHT
    || mode === MODES.TOP_RIGHT
    || mode === MODES.BOTTOM_RIGHT
);
const isTopResize = mode => (
    mode === MODES.TOP
    || mode === MODES.TOP_LEFT
    || mode === MODES.TOP_RIGHT
);
const isBottomResize = mode => (
    mode === MODES.BOTTOM
    || mode === MODES.BOTTOM_LEFT
    || mode === MODES.BOTTOM_RIGHT
);
const isHorizontalResize = mode => (
    mode !== MODES.TOP
    && mode !== MODES.BOTTOM
    && mode !== MODES.MOVE
);

// eslint-disable-next-line complexity
const resolveAdjustments = (targetSize, constrainedSize, mode) => {
    const resolved = { ...constrainedSize };

    if (isTopResize(mode) || mode === MODES.MOVE) {
        resolved.top -= constrainedSize.height - targetSize.height;
        // if we're not dragging and our top has been constrained, add to height
        if (mode !== MODES.MOVE && targetSize.top !== constrainedSize.top) {
            resolved.height += targetSize.top - constrainedSize.top;
        }
    }

    if (isBottomResize(mode)) {
        resolved.height += constrainedSize.top - targetSize.top;
        // if our top has been constrained during bottom resize, undo it
        if (targetSize.top !== constrainedSize.top) {
            resolved.top = targetSize.top;
        }
    }

    if (isLeftSideResize(mode) || mode === MODES.MOVE) {
        resolved.left -= constrainedSize.width - targetSize.width;
        // if we're not dragging and our left side has been constrained, add to width
        if (mode !== MODES.MOVE && targetSize.left !== constrainedSize.left) {
            resolved.width += targetSize.left - constrainedSize.left;
        }
    }

    if (isRightSideResize(mode)) {
        resolved.width += constrainedSize.left - targetSize.left;
        // if our left has been constrained during a right side resize, undo it
        if (targetSize.left !== constrainedSize.left) {
            resolved.left = targetSize.left;
        }
    }

    return resolved;
};
export const getFlipMode = (flipH, flipV, mode) => {
    if (flipH === -1) {
        if (isLeftSideResize(mode)) {
            mode = mode.replace('LEFT', 'RIGHT');
        } else if (isRightSideResize(mode)) {
            mode = mode.replace('RIGHT', 'LEFT');
        }
    }
    if (flipV === -1) {
        if (isTopResize(mode)) {
            mode = mode.replace('TOP', 'BOTTOM');
        } else if (isBottomResize(mode)) {
            mode = mode.replace('BOTTOM', 'TOP');
        }
    }
    return mode;
};

export const constrainWidthHeight = (nextSize, minWidth, minHeight, mode) => {
    const constrained = { ...nextSize };
    constrained.width = Math.max(minWidth, nextSize.width);
    constrained.height = Math.max(minHeight, nextSize.height);
    return resolveAdjustments(nextSize, constrained, mode);
};

export const constrainCanvasBounds = (nextSize, canvasWidth, canvasHeight, mode) => {
    const padding = 20;
    const constrained = { ...nextSize };

    // Prevent leaving bottom
    if (constrained.top + padding > canvasHeight) {
        constrained.top = canvasHeight - padding;
    }
    // Prevent leaving right
    if (constrained.left + padding > canvasWidth) {
        constrained.left = canvasWidth - padding;
    }
    // Prevent leaving top
    if (constrained.top + constrained.height - padding < 0) {
        constrained.top = -constrained.height + padding;
    }
    // Prevent leaving left
    if (constrained.left + constrained.width - padding < 0) {
        constrained.left = -constrained.width + padding;
    }

    return resolveAdjustments(nextSize, constrained, mode);
};

function cacuVerticalLeft(mode, outer, lines) {
    if (!mode || mode === MODES.MOVE || isLeftSideResize(mode)) {
        const verticalLeft = [outer.left, 1, SNAP_DISTANCE, COORDINATE.LEFT_TOP, false];
        lines.push(verticalLeft);
    }
}

function cacuVerticalMid(mode, outer, fromSlide, lines) {
    if (!mode || mode === MODES.MOVE) {
        const verticalMid = [outer.left + (outer.width / 2), 1, SNAP_DISTANCE, COORDINATE.LEFT_MID, fromSlide];
        lines.push(verticalMid);
    }
}

function cacuVerticalRight(mode, outer, lines) {
    if (!mode || mode === MODES.MOVE || isRightSideResize(mode)) {
        const verticalRight = [outer.left + outer.width, 1, SNAP_DISTANCE, COORDINATE.LEFT_BOT, false];
        lines.push(verticalRight);
    }
}

function cacuHorizontalTop(mode, outer, lines) {
    if (!mode || mode === MODES.MOVE || isTopResize(mode)) {
        const horizontalTop = [outer.top, 0, SNAP_DISTANCE, COORDINATE.TOP_LEFT, false];
        lines.push(horizontalTop);
    }
}

function cacuHorizontalMid(mode, outer, fromSlide, lines) {
    if (!mode || mode === MODES.MOVE) {
        const horizontalMid = [outer.top + (outer.height / 2), 0, SNAP_DISTANCE, COORDINATE.TOP_MIDD, fromSlide];
        lines.push(horizontalMid);
    }
}

function cacuHorizontalBottom(mode, outer, lines) {
    if (!mode || mode === MODES.MOVE || isBottomResize(mode)) {
        const horizontalBottom = [outer.top + outer.height, 0, SNAP_DISTANCE, COORDINATE.TOP_RIGT, false];
        lines.push(horizontalBottom);
    }
}

const computeLines = (mode, outer, lines, fromSlide) => {
    // Depending on the mode/direction of the drag, determine what lines to use for snapping
    cacuVerticalLeft(mode, outer, lines);
    cacuVerticalMid(mode, outer, fromSlide, lines);
    cacuVerticalRight(mode, outer, lines);
    cacuHorizontalTop(mode, outer, lines);
    cacuHorizontalMid(mode, outer, fromSlide, lines);
    cacuHorizontalBottom(mode, outer, lines);
    lines.push(outer);
};

export const rectToSnapLines = (rect, mode) => {
    const lines = [];
    let fromSlide = false;
    if (rect?.source) {
        fromSlide = true;
    }
    const outer = rect.rotate ? getFrameAfterRotate(rect, rect.rotate) : rect;
    if (outer.left === 0) {
        outer.left = 0.01;
    }
    if (outer.top === 0) {
        outer.top = 0.01;
    }

    computeLines(mode, outer, lines, fromSlide);

    return { lines };
};

export const rectsToSnapLines = (rects) => {
    const snaps = [];
    rects.forEach((rect) => {
        const lines = rectToSnapLines(rect);
        if (lines) {
            snaps.push(lines);
        }
    });
    return snaps;
};

function getDifference(a, b) {
    return Math.abs(a - b);
}

// eslint-disable-next-line complexity
export const getClosestSnapLines = (snaps, itemSnapLines, mode) => {
    let closestHorizontal = [];
    let closestVertical = [];

    function getBinggo(closestLength, tempCloset) {
        let edgeBingo = -1;
        let midBingo = -1;

        for (let k = 0; k < closestLength; k++) {
            const oldEdge = closestVertical[k].edge;
            if (oldEdge[0] === tempCloset.edge[0]) {
                edgeBingo = k;
            }
            if (isMidLine(closestVertical[k].line[3])) {
                midBingo = k;
            }
        }
        return { edgeBingo, midBingo };
    }

    function setVerticalFrame(edgeBingo, tempCloset, fm) {
        const frame = fm;
        if (closestVertical[edgeBingo].distance > tempCloset.distance
            && Math.abs(
                closestVertical[edgeBingo].distance - tempCloset.distance) >= 0.01) {
            closestVertical[edgeBingo] = tempCloset;
        }
        // step5 命中edge时的线重合 更新frame
        else if (Math.abs(
            closestVertical[edgeBingo].distance - tempCloset.distance) < 0.01) {
            const oldFrame = closestVertical[edgeBingo].frame;
            frame.top = oldFrame.top < frame.top ? oldFrame.top : frame.top;
            frame.bottom = oldFrame.bottom > frame.bottom ? oldFrame.bottom : frame.bottom;
            closestVertical[edgeBingo].frame = frame;
        }
    }

    function setClosestVertical(tempCloset, midBingo) {
        // step 8 边线distance更小，清空array然后插入
        if (tempCloset.distance < closestVertical[midBingo].distance) {
            closestVertical = [];
            closestVertical.push(tempCloset);
        } else if (Math.abs(
            closestVertical[midBingo].distance - tempCloset.distance) < 0.01) {
            // step 9 distance相同pop掉非slide中线
            if (!closestVertical[midBingo].line[4]) {
                closestVertical.pop(midBingo);
            }
            closestVertical.push(tempCloset);
        }
    }

    function setClosestVertical2(tempCloset) {
        if (tempCloset.distance < closestVertical[0].distance
            && Math.abs(closestVertical[0].distance - tempCloset.distance) >= 0.01) {
            closestVertical = [];
            closestVertical.push(tempCloset);
        } else if (Math.abs(closestVertical[0].distance - tempCloset.distance) < 0.01) {
            closestVertical.push(tempCloset);
        }
    }

    for (let i = 0; i < snaps.length; i++) {
        // 取出存线时传入的矩形
        const r1 = snaps[i].lines[snaps[i].lines.length - 1];
        for (let j = 0; j < snaps[i].lines.length - 1; j++) {
            // 依次取出根据屏幕中已有元素计算出存入的直线：每个元素六条
            const s1 = snaps[i].lines[j];
            // 取出当前拖动元素对应的矩形
            const r2 = itemSnapLines.lines[itemSnapLines.lines.length - 1];
            for (let x = 0; x < itemSnapLines.lines.length - 1; x++) {
                // 依次取出当前拖动元素对应的六条直线
                const s2 = itemSnapLines.lines[x];
                if (s1[1] === s2[1]) { // 是同一个方向的直线
                    const distance = getDifference(s1[0], s2[0]);
                    if (distance < s2[2]) { // 达到触发吸附的距离
                        if (s1[1] === 0) { // 水平线
                            const closestLength = closestHorizontal.length;
                            // step1 计算当前两个图形的左右边界
                            let frame = [];
                            if (r1?.source) {
                                frame = {
                                    left: r1.left < r2.left ? r1.left : r2.left,
                                    right: r1.left + r1.width > r2.left + r2.width
                                        ? r1.left + r1.width : r2.left + r2.width,
                                };
                            } else {
                                frame = {
                                    left: r1.left < r2.left ? r1.left - 10 : r2.left - 10,
                                    right: r1.left + r1.width > r2.left + r2.width
                                        ? r1.left + r1.width + 10 : r2.left + r2.width + 10,
                                };
                            }

                            const tempCloset = {
                                line: s1.slice(0, -2).concat(s2[3])
                                    .concat(s1[4]), // 这里将标记替换为当前拖动元素对应的直线，方便后面修改坐标
                                edge: s2,
                                distance: (s2[3] === COORDINATE.TOP_RIGT && mode !== MODES.MOVE)
                                    ? distance / 2 : distance,
                                frame,
                            };
                            // step2 命中数组为空直接插入
                            if (closestLength === 0) {
                                closestHorizontal.push(tempCloset);
                            } else {
                                let edgeBingo = -1;
                                let midBingo = -1;
                                // step3 命中数组不为空时判断当前edge是否命中过
                                for (let k = 0; k < closestLength; k++) {
                                    const oldEdge = closestHorizontal[k].edge;
                                    if (oldEdge[0] === tempCloset.edge[0]) {
                                        edgeBingo = k;
                                    }
                                    if (isMidLine(closestHorizontal[k].line[3])) {
                                        midBingo = k;
                                    }
                                }
                                if (edgeBingo !== -1) {
                                    // step4 命中edge时保留距离更近的
                                    // eslint-disable-next-line max-len
                                    if (closestHorizontal[edgeBingo].distance > tempCloset.distance && Math.abs(closestHorizontal[edgeBingo].distance - tempCloset.distance) >= 0.01) {
                                        closestHorizontal[edgeBingo] = tempCloset;
                                    }
                                    // step5 命中edge时的线重合 更新frame
                                    // eslint-disable-next-line max-len
                                    else if (Math.abs(closestHorizontal[edgeBingo].distance - tempCloset.distance) < 0.01) {
                                        const oldFrame = closestHorizontal[edgeBingo].frame;
                                        frame.left = oldFrame.left < frame.left ? oldFrame.left : frame.left;
                                        frame.right = oldFrame.right > frame.right ? oldFrame.right : frame.right;
                                        closestHorizontal[edgeBingo].frame = frame;
                                    }
                                    continue;
                                } else {
                                    // step6 未命中edge且插入的为中线 则不插入
                                    if (isMidLine(s2[3])) {
                                        // slide中线继续插入
                                        if (s1[4]) {
                                            if (Math.abs(closestHorizontal[0].distance - tempCloset.distance) < 0.01) {
                                                closestHorizontal.push(tempCloset);
                                            } else if (tempCloset.distance < closestHorizontal[0].distance) {
                                                closestHorizontal = [];
                                                closestHorizontal.push(tempCloset);
                                            }
                                        } else {
                                            continue;
                                        }
                                    } else if (midBingo !== -1) {  // step7 未命中edge且插入的为边线
                                        // step 8 边线distance更小，清空array然后插入
                                        if (tempCloset.distance < closestHorizontal[midBingo].distance) {
                                            closestHorizontal = [];
                                            closestHorizontal.push(tempCloset);
                                        } else if (
                                            Math.abs(closestHorizontal[midBingo].distance - tempCloset.distance) < 0.01
                                        ) {
                                            // step 9 distance相同pop掉非slide中线
                                            if (!closestHorizontal[midBingo].line[4]) {
                                                closestHorizontal.pop(midBingo);
                                            }
                                            closestHorizontal.push(tempCloset);
                                        }
                                    } else { // step 10 未命中edge 插入的是边线 已插入未命中中线
                                        if (tempCloset.distance < closestHorizontal[0].distance
                                            && Math.abs(closestHorizontal[0].distance - tempCloset.distance) >= 0.01) {
                                            closestHorizontal = [];
                                            closestHorizontal.push(tempCloset);
                                        } else if (
                                            Math.abs(closestHorizontal[0].distance - tempCloset.distance) < 0.01
                                        ) {
                                            closestHorizontal.push(tempCloset);
                                        }
                                    }
                                }
                            }
                        } else if (s1[1] === 1) { // 垂直线
                            const closestLength = closestVertical.length;
                            // step1 计算当前两个图形的上下边界
                            let frame = {};
                            if (r1?.source) {
                                frame = {
                                    top: r1.top < r2.top ? r1.top : r2.top,
                                    bottom: r1.top + r1.height > r2.top + r2.height
                                        ? r1.top + r1.height : r2.top + r2.height,
                                };
                            } else {
                                frame = {
                                    top: r1.top < r2.top ? r1.top - 10 : r2.top - 10,
                                    bottom: r1.top + r1.height > r2.top + r2.height
                                        ? r1.top + r1.height + 10 : r2.top + r2.height + 10,
                                };
                            }


                            const tempCloset = {
                                line: s1.slice(0, -2).concat(s2[3])
                                    .concat(s1[4]), // 这里将标记替换为当前拖动元素对应的直线，方便后面修改坐标
                                edge: s2,
                                distance: (s2[3] === COORDINATE.LEFT_BOT && mode !== MODES.MOVE)
                                    ? distance / 2 : distance,
                                frame,
                            };
                            // step2 命中数组为空直接插入
                            if (closestLength === 0) {
                                closestVertical.push(tempCloset);
                            } else {
                                // step3 命中数组不为空时判断当前edge是否命中过
                                const { edgeBingo, midBingo } = getBinggo(closestLength, tempCloset);
                                if (edgeBingo !== -1) {
                                // step4 命中edge时保留距离更近的
                                    setVerticalFrame(edgeBingo, tempCloset, frame);
                                    continue;
                                } else {
                                // step6 未命中edge且插入的为中线 则不插入
                                    if (isMidLine(s2[3])) {
                                        // slide中线继续插入
                                        if (s1[4]) {
                                            if (Math.abs(closestVertical[0].distance - tempCloset.distance) < 0.01) {
                                                closestVertical.push(tempCloset);
                                            } else if (tempCloset.distance < closestVertical[0].distance) {
                                                closestVertical = [];
                                                closestVertical.push(tempCloset);
                                            }
                                        } else {
                                            continue;
                                        }
                                    } else if (midBingo !== -1) { // step7 未命中edge且插入的为边线 插入边线并把已插入的中线移除
                                        setClosestVertical(tempCloset, midBingo);
                                    } else { // 未命中edge 插入的是边线 已插入未命中中线
                                        setClosestVertical2(tempCloset);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return {
        horizontal: closestHorizontal,
        vertical: closestVertical,
    };
};

function isMidLine(type) {
    return type === COORDINATE.LEFT_MID || type === COORDINATE.TOP_MIDD;
}

export const constrainRatio = (originalSize, nextSize, snapResults, mode) => {
    const constrained = { ...nextSize };
    const ratio = originalSize.width / originalSize.height;

    const isVert = isVerticalResize(mode);
    const isHor = isHorizontalResize(mode);

    if ((isHor && !isVert)
        || (isVert && !snapResults.horizontal
        || (snapResults.vertical && snapResults.horizontall
            && snapResults.vertical.distance < snapResults.horizontal.distance))) {
        constrained.height = nextSize.width / ratio;
    } else {
        constrained.width = nextSize.height * ratio;
    }

    const size = resolveAdjustments(nextSize, constrained, mode);
    return size;
};

// eslint-disable-next-line complexity
export const constrainGrid = (nextSize, snapLines, mode) => {
    const constrained = { ...nextSize };
    const itemSnapLines = rectToSnapLines(constrained, mode);
    const closest = getClosestSnapLines(snapLines, itemSnapLines, mode);
    const lines = [];
    let dy = 0;
    let dx = 0;


    if (closest.horizontal && closest.horizontal.length > 0) {
        const hor = closest.horizontal;
        for (let i = 0; i < hor.length; i++) {
            dy = hor[i].line[0] - hor[i].edge[0];
            // if we're not dragging and we're on a mid point, we need to double the delta.
            if (mode !== MODES.MOVE
                && (hor[i].edge[0] !== constrained.top && hor[i].edge[0] !== constrained.height + constrained.top)) {
                dy *= 2;
            }
            hor[i].line.push(hor[i].frame);
            lines.push(hor[i].line);
        }
    }

    if (closest.vertical && closest.vertical.length > 0) {
        const ver = closest.vertical;
        for (let i = 0; i < ver.length; i++) {
            dx = ver[i].line[0] - ver[i].edge[0];
            // if we're not dragging and we're on a mid point, we need to double the delta.
            if (mode !== MODES.MOVE
                && (ver[i].edge[0] !== constrained.left && ver[i].edge[0] !== constrained.width + constrained.left)) {
                dx *= 2;
            }
            ver[i].line.push(ver[i].frame);
            lines.push(ver[i].line);
        }
    }

    constrained.top += dy;
    constrained.left += dx;

    const size = resolveAdjustments(nextSize, constrained, mode);

    return {
        size,
        closest,
        lines,
    };
};

export const testObj = {
    isVerticalResize,
    isLeftSideResize,
    isRightSideResize,
    isTopResize,
    isBottomResize,
    isHorizontalResize,
    resolveAdjustments,
    computeLines,
};
